import type { ReactNode } from "react";
import { CurrencyIcon } from "@/components/ui/CurrencyIcons";

/** Single-row coins + gems inside one dock pill (shared rim & groove). */
export function HeaderCoinsGemsBar({
  coins,
  gems,
  className = "",
  compactOnDesktop = false,
}: {
  coins: string;
  gems: string;
  /** Extra classes on the outer pill (e.g. ring, shadow). */
  className?: string;
  /** Tighter pill on `md+` (e.g. prize shop hero). */
  compactOnDesktop?: boolean;
}) {
  const shell =
    "rounded-full border-2 border-[#4a90e2] bg-[linear-gradient(180deg,#2c5e8c_0%,#245882_18%,#1a4a7a_50%,#245882_82%,#2c5e8c_100%)] shadow-[inset_0_2px_3px_rgba(255,255,255,0.22),inset_0_-4px_10px_rgba(0,0,0,0.38)]";
  const valueClass = compactOnDesktop
    ? "font-display text-[1.05rem] font-black tabular-nums tracking-tight text-[#fde68a] [text-shadow:0_1px_0_#1a1003,0_2px_0_#0f0a04,1px_0_0_#1a1003,-1px_0_0_#1a1003] sm:text-[1.12rem] md:text-[0.72rem]"
    : "font-display min-w-0 max-w-full text-[clamp(0.8125rem,0.42rem+2.1vw,1.05rem)] font-black tabular-nums tracking-tight text-[#fde68a] [text-shadow:0_1px_0_#1a1003,0_2px_0_#0f0a04,1px_0_0_#1a1003,-1px_0_0_#1a1003] sm:text-[1.12rem]";

  const seg = compactOnDesktop
    ? "flex min-w-0 flex-1 items-center justify-center gap-2.5 px-2 py-1.5 sm:gap-3 sm:px-4 sm:py-2 md:gap-1 md:px-1.5 md:py-0"
    : "flex min-w-0 flex-1 items-center justify-center gap-1.5 px-1.5 py-1.5 sm:gap-3 sm:px-4 sm:py-2";
  const iconSlot = compactOnDesktop
    ? "relative z-[1] flex h-[2.75rem] w-[2.75rem] shrink-0 items-center justify-center overflow-visible sm:h-[3.1rem] sm:w-[3.1rem] md:h-[1.55rem] md:w-[1.55rem]"
    : "relative z-[1] flex h-[2.35rem] w-[2.35rem] shrink-0 items-center justify-center overflow-visible sm:h-[3.1rem] sm:w-[3.1rem]";
  const dividerMy = compactOnDesktop
    ? "my-2.5 md:my-0.5"
    : "my-2.5";
  const rootMinH = compactOnDesktop
    ? "min-h-[3rem] sm:min-h-[3.25rem] md:min-h-0"
    : "min-h-[3rem] sm:min-h-[3.25rem]";

  return (
    <div
      className={`flex ${rootMinH} w-full min-w-0 max-w-full items-stretch ${shell} ${className}`}
    >
      <div className={seg} aria-label={`Coins: ${coins}`}>
        <span className={iconSlot} aria-hidden>
          <CurrencyIcon
            kind="coin"
            size={112}
            priority
            className={`h-full w-full origin-center scale-[1.14] object-contain saturate-[1.12] brightness-105 drop-shadow-[0_4px_3px_rgba(0,0,0,0.55)] drop-shadow-[0_0_14px_rgba(253,224,71,0.35)] ${compactOnDesktop ? "md:scale-[0.72]" : ""}`}
          />
        </span>
        <span
          className={`${valueClass} min-w-0 max-w-full shrink text-center max-sm:truncate sm:shrink-0 sm:whitespace-nowrap sm:text-left`}
        >
          {coins}
        </span>
      </div>
      <div
        className={`${dividerMy} w-px shrink-0 self-stretch rounded-full bg-gradient-to-b from-white/10 via-white/30 to-white/10 shadow-[1px_0_0_rgba(0,0,0,0.45)]`}
        aria-hidden
      />
      <div className={seg} aria-label={`Gems: ${gems}`}>
        <span className={iconSlot} aria-hidden>
          <CurrencyIcon
            kind="gem"
            size={112}
            priority
            className={`h-full w-full origin-center scale-[1.14] object-contain saturate-[1.15] brightness-105 drop-shadow-[0_4px_3px_rgba(0,0,0,0.5)] drop-shadow-[0_0_16px_rgba(192,132,252,0.42)] ${compactOnDesktop ? "md:scale-[0.72]" : ""}`}
          />
        </span>
        <span
          className={`${valueClass} min-w-0 max-w-full shrink text-center max-sm:truncate sm:shrink-0 sm:whitespace-nowrap sm:text-left`}
        >
          {gems}
        </span>
      </div>
    </div>
  );
}

/**
 * Outer profile-shell blue gradient bezel (matches `/profile` hero card chrome).
 */
export function BlueGradientBezel({
  children,
  className = "",
  topGlossClassName = "",
}: {
  children: ReactNode;
  className?: string;
  /** Appended to the top gloss overlay (e.g. `md:hidden` when the bezel is transparent on desktop). */
  topGlossClassName?: string;
}) {
  return (
    <div
      className={`relative w-full max-w-full rounded-[26px] border-[5px] border-[#0C4779] bg-gradient-to-b from-[#4BADEB] via-[#258BD4] to-[#135C9A] p-4 shadow-[0_10px_0_rgba(8,47,74,0.55),0_22px_42px_rgba(0,0,0,0.42),inset_0_2px_0_rgba(255,255,255,0.28)] sm:p-5 ${className}`}
    >
      <div
        className={`pointer-events-none absolute inset-x-[10px] top-[6px] z-0 h-[42%] rounded-t-[20px] bg-gradient-to-b from-white/28 to-transparent sm:inset-x-[12px] ${topGlossClassName}`}
        aria-hidden
      />
      <div className="relative z-[1]">{children}</div>
    </div>
  );
}

/** Reference pill: vertical groove (#2c5e8c → #1a4a7a), `#4a90e2` rim, sticker text. */
export function HeaderStatBar({
  label,
  value,
  tone = "solid",
}: {
  label: ReactNode;
  value: string;
  tone?: "solid" | "dockGradient" | "cream";
}) {
  const dockGradient = {
    shell:
      "rounded-full border-2 border-[#4a90e2] bg-[linear-gradient(180deg,#2c5e8c_0%,#245882_18%,#1a4a7a_50%,#245882_82%,#2c5e8c_100%)] shadow-[inset_0_2px_3px_rgba(255,255,255,0.22),inset_0_-4px_10px_rgba(0,0,0,0.38)]",
    labelClass:
      "font-display text-[10px] font-black uppercase tracking-[0.1em] text-[#e8f4ff] [text-shadow:0_1px_0_#020617,0_2px_0_#020617,1px_0_0_#020617,-1px_0_0_#020617,1px_1px_0_#020617,-1px_-1px_0_#020617]",
    valueClass:
      "font-display text-[0.95rem] font-black tabular-nums tracking-tight text-[#fde68a] [text-shadow:0_1px_0_#1a1003,0_2px_0_#0f0a04,1px_0_0_#1a1003,-1px_0_0_#1a1003]",
  } as const;

  const cream = {
    shell:
      "rounded-[12px] border-2 border-[#c9a574] bg-[#FFF7DD] shadow-[inset_0_2px_8px_rgba(60,35,18,0.12)]",
    labelClass:
      "font-display text-[11px] font-black uppercase tracking-[0.07em] text-[#4a2818] sm:text-[0.8125rem]",
    valueClass:
      "font-display text-[1.05rem] font-black tabular-nums tracking-tight text-[#5c2e0c] sm:text-[1.125rem] " +
      "rounded-full border-2 border-[#a16207] bg-gradient-to-b from-[#fef9c3] via-[#facc15] to-[#ca8a04] " +
      "px-2.5 py-0.5 shadow-[inset_0_2px_0_rgba(255,255,255,0.55),0_2px_0_rgba(120,53,15,0.28)]",
  } as const;

  const palette =
    tone === "dockGradient"
      ? dockGradient
      : tone === "cream"
        ? cream
        : null;

  const shell =
    palette?.shell ??
    "rounded-[10px] border border-black/15 bg-[#124F82]/92 shadow-[inset_0_2px_4px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.18)]";

  return (
    <div
      className={`flex min-h-[2.65rem] items-center justify-between gap-3 px-3 py-2.5 sm:min-h-[2.85rem] sm:px-3.5 sm:py-3 ${shell}`}
    >
      <span
        className={
          palette?.labelClass ??
          "font-display text-[10px] font-extrabold uppercase tracking-[0.12em] text-sky-100/95"
        }
      >
        {label}
      </span>
      <span
        className={
          palette?.valueClass ??
          "font-display text-[0.95rem] font-black tabular-nums tracking-tight text-[#FFD044] drop-shadow-[0_2px_0_rgba(78,43,33,0.45)]"
        }
      >
        {value}
      </span>
    </div>
  );
}

export function CreamPanel(props: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-[14px] border-2 border-[#d4b48a] bg-[#FFF0C8] shadow-[inset_0_2px_0_rgba(255,247,221,0.9),0_4px_0_rgba(78,43,33,0.12)] ${props.className ?? ""}`}
    >
      {props.children}
    </div>
  );
}

export function CreamSectionHeader(props: {
  title: string;
  className?: string;
  titleClassName?: string;
}) {
  return (
    <div
      className={`rounded-t-[12px] bg-gradient-to-b from-[#efd4a8] to-[#E2BD8A] px-3 py-2.5 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${props.className ?? ""}`}
    >
      <span
        className={`font-display text-[11px] font-black uppercase tracking-[0.16em] text-[#7B422F] drop-shadow-[0_1px_0_rgba(255,247,221,0.65)] ${props.titleClassName ?? ""}`}
      >
        {props.title}
      </span>
    </div>
  );
}
